import React from 'react';
import { LineItem } from '../types';

export interface ItemsPreviewCellProps {
  items: LineItem[];
}

// Fallback for items with no imageUrl — a real, resolvable Home Depot static asset (a generic
// "no image" glyph), matching production's treatment: a missing image still gets its own
// thumbnail slot, never a name fallback or a silently-skipped slot. Shared with
// ExpandedOrderDetailsPanel's LineItemThumbnail.
export const UNAVAILABLE_IMAGE_URL = 'https://assets.thdstatic.com/images/v1/no-img.svg';

// Only the first N items get an image slot; the rest collapse into a single "+N" chip — matches
// production's Products column (a compact preview, not the fuller thumbnail strip
// ExpandedOrderDetailsPanel shows once a row is expanded).
const MAX_VISIBLE_ITEMS = 2;

// Thumbnails/chip are `shrink-0`/`min-w-10` so a narrow column can't squash them into overlapping the
// next cell (Status badge) — the cell grows to fit instead.
// Images only, no item name/"+N more" text — matches production's Products column
// (ProductImages), which shows just the thumbnail strip.
export const ItemsPreviewCell: React.FC<ItemsPreviewCellProps> = ({ items }) => {
  if (!items.length) {
    return null;
  }

  const visibleItems = items.slice(0, MAX_VISIBLE_ITEMS);
  const hiddenItemCount = items.length - visibleItems.length;

  return (
    <span className="sui-inline-flex sui-flex-nowrap sui-items-center sui-gap-1">
      {visibleItems.map((item, index) => (
        <img
          key={`${item.imageUrl ?? 'no-image'}-${index}`}
          src={item.imageUrl || UNAVAILABLE_IMAGE_URL}
          alt={item.name ?? 'no-image'}
          width={40}
          height={40}
          className="sui-h-10 sui-w-10 sui-min-w-10 sui-shrink-0 sui-rounded-base sui-border-1 sui-border-solid sui-border-primary sui-object-cover"
        />
      ))}
      {hiddenItemCount > 0 && (
        <span className="sui-flex sui-h-10 sui-min-w-10 sui-shrink-0 sui-items-center sui-justify-center sui-rounded-base sui-bg-subtle sui-px-1 sui-text-sm sui-font-bold sui-text-primary">
          {`+${hiddenItemCount}`}
        </span>
      )}
    </span>
  );
};
